import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useState } from 'react';
import { Handles, Rail, Slider as SliderCompound, Ticks, Tracks } from 'react-compound-slider';
import styled from 'styled-components';
import { SliderRail, SliderTickDot, SliderTickLabel, SliderTrack } from './components/';
import { SliderHandle } from './components';
import { getPx } from '../../helpers/common';
import { SLIDER_TICK_LABEL_TOP_POSITION } from './constants';
import { BUTTON_CIRCLE_SIZE_TINY } from '../../Button/constants';
import { useDidUpdateEffect, useMediaBreakpoints } from '../../hooks';
import { EMPTY_PROPS, FORMAT_FUNCTION } from '../../helpers/types';
import { equalArrays } from '../../helpers/arrays';
import { isDefined } from '../../helpers/defined';
import { baseHighlightStyle } from '../../tokens/highlight';
import { height } from '../../style/height';
export const SliderWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  flex-grow: 1;
  ${({
  $fullWidth
}) => $fullWidth && `width: 100%;`}
  ${baseHighlightStyle}
  ${height(BUTTON_CIRCLE_SIZE_TINY)}
  ${({
  $relative,
  $tickMaxHeight,
  $tickTopPosition,
  $sliderHeight
}) => $relative && `padding-bottom: ${getPx($tickMaxHeight + $tickTopPosition - $sliderHeight / 2)};`}
`;
const getCustomFormatTicks = values => {
  return Object.keys(values).map(val => parseInt(val, 10));
};
const Slider = ({
  alignLabels = false,
  relative = true,
  fullWidth = false,
  range = false,
  allowCrossing = false,
  disabled = false,
  value,
  onChange,
  onUpdate,
  min = 0,
  max = 10,
  step = 1,
  showTicks = false,
  showTicksWithLabels = false,
  customTicksLabels,
  ticksValues,
  clickableTicks = false,
  format = FORMAT_FUNCTION,
  handleProps = EMPTY_PROPS,
  handleFromProps = EMPTY_PROPS,
  handleToProps = EMPTY_PROPS,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(range ? Array.isArray(value) ? value : [min, min + 1] : typeof value === 'number' ? value : min);
  const domain = [min, max];
  const values = range ? internalValue : [internalValue];
  const customTicksValues = ticksValues || customTicksLabels ? ticksValues || getCustomFormatTicks(customTicksLabels) : undefined;

  // notify about changes
  useDidUpdateEffect(() => {
    onChange?.(internalValue);
  }, [internalValue]);

  // react on changes from outside
  useDidUpdateEffect(() => {
    if (!range) {
      if (value !== internalValue) {
        setInternalValue(typeof value === 'number' ? value : min);
      }
    } else {
      if (!equalArrays(value, internalValue)) {
        setInternalValue(Array.isArray(value) ? value : [min, min + 1]);
      }
    }
  }, [value]);
  const ticksCount = !ticksValues && isDefined(min) && isDefined(max) && step ? Math.ceil((max - min) / step) + 1 : undefined;
  const formatHandleProps = (index, handlesLength) => {
    if (handlesLength === 1) {
      return handleProps;
    } else {
      if (index === 0) {
        return handleFromProps;
      } else {
        return handleToProps;
      }
    }
  };
  const onTickClick = useCallback(value => {
    let newValue;
    if (range) {
      if (value >= internalValue[1]) {
        newValue = [internalValue[0], value];
      } else if (value <= internalValue[0]) {
        newValue = [value, internalValue[1]];
      } else {
        if (internalValue[1] - value < value - internalValue[0]) {
          newValue = [internalValue[0], value];
        } else {
          newValue = [value, internalValue[1]];
        }
      }
    } else {
      newValue = value;
    }
    setInternalValue(newValue);
    onUpdate?.(newValue);
  }, [range, internalValue, onUpdate]);
  const [tickMaxHeight, setTickMaxHeight] = useState(0);
  const tickTopPosition = useMediaBreakpoints(SLIDER_TICK_LABEL_TOP_POSITION);
  const sliderHeight = useMediaBreakpoints(BUTTON_CIRCLE_SIZE_TINY);
  const measureTickHeight = useCallback(height => {
    if (height && height > tickMaxHeight) {
      setTickMaxHeight(height);
    }
  }, [tickMaxHeight]);
  return /*#__PURE__*/React.createElement(SliderWrapper, _extends({
    $tickTopPosition: tickTopPosition,
    $tickMaxHeight: tickMaxHeight,
    $relative: relative && (showTicksWithLabels || customTicksLabels),
    $fullWidth: fullWidth,
    $sliderHeight: sliderHeight
  }, props), /*#__PURE__*/React.createElement(SliderCompound, {
    disabled: disabled,
    mode: allowCrossing ? 1 : 2,
    step: step,
    domain: domain,
    rootStyle: {
      position: 'relative',
      width: '100%',
      touchAction: 'none'
    },
    onChange: newValue => {
      if (range) {
        setInternalValue(newValue);
      } else {
        setInternalValue(newValue[0]);
      }
    },
    onUpdate: newValue => {
      if (range) {
        onUpdate?.(newValue);
      } else {
        onUpdate?.(newValue[0]);
      }
    },
    values: values
  }, /*#__PURE__*/React.createElement(Rail, null, ({
    getRailProps
  }) => /*#__PURE__*/React.createElement(SliderRail, {
    disabled: disabled,
    getRailProps: getRailProps
  })), (showTicks || showTicksWithLabels) && /*#__PURE__*/React.createElement(Ticks, {
    count: ticksCount,
    values: customTicksValues
  }, ({
    ticks
  }) => /*#__PURE__*/React.createElement(React.Fragment, null, ticks.map(tick => /*#__PURE__*/React.createElement("span", {
    key: `${tick.id}-tick`
  }, (showTicks || showTicksWithLabels) && /*#__PURE__*/React.createElement(SliderTickDot, {
    tick: tick
  }), (showTicksWithLabels || customTicksLabels) && (customTicksLabels ? /*#__PURE__*/React.createElement(SliderTickLabel, {
    key: `${tick.id}-label`,
    align: alignLabels,
    tick: tick,
    count: ticks.length,
    format: tick => customTicksLabels[tick],
    onClick: clickableTicks ? onTickClick : null,
    measureHeight: measureTickHeight
  }) : /*#__PURE__*/React.createElement(SliderTickLabel, {
    key: `${tick.id}-label`,
    align: alignLabels,
    tick: tick,
    count: ticks.length,
    format: format,
    onClick: clickableTicks ? onTickClick : null,
    measureHeight: measureTickHeight
  })))))), /*#__PURE__*/React.createElement(Tracks, {
    right: false,
    left: !range
  }, ({
    tracks,
    getTrackProps
  }) => /*#__PURE__*/React.createElement(React.Fragment, null, tracks.map(({
    id,
    source,
    target
  }) => /*#__PURE__*/React.createElement(SliderTrack, {
    disabled: disabled,
    key: id,
    source: source,
    target: target,
    getTrackProps: getTrackProps
  })))), /*#__PURE__*/React.createElement(Handles, null, ({
    handles,
    getHandleProps
  }) => /*#__PURE__*/React.createElement(React.Fragment, null, handles.map((handle, index) => /*#__PURE__*/React.createElement(SliderHandle, _extends({
    disabled: disabled,
    key: handle.id,
    handle: handle,
    domain: domain,
    getHandleProps: getHandleProps
  }, formatHandleProps(index, handles.length))))))));
};
export default Slider;